import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";

export default function JSCodeEditorOutput({
  code,
  loading = false,
}) {
  const [output, setOutput] = useState([]);
  const muiTheme = useTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";

  const outputBg = isDarkMode ? "#0d1117" : "#f6f8fa";
  const outputText = isDarkMode ? "#3fb950" : "#0969da";
  const errorText = isDarkMode ? "#ff7b72" : "#cf222e";

  useEffect(() => {
    if (!code || !code.trim()) {
      setOutput([]);
      return;
    }

    let isCancelled = false;

    const runJS = async () => {
      const rawLogs = [];

      const formatArg = (arg) => {
        if (arg === null) return "null";
        if (arg === undefined) return "undefined";
        if (typeof arg === "function") return `[Function: ${arg.name || "anonymous"}]`;
        if (typeof arg === "symbol") return arg.toString();
        if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack || ""}`;
        if (typeof arg === "object") {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      };

      const customConsole = {
        log: (...args) => {
          rawLogs.push({ text: args.map(formatArg).join(" "), isError: false });
        },
        info: (...args) => {
          rawLogs.push({ text: args.map(formatArg).join(" "), isError: false });
        },
        warn: (...args) => {
          rawLogs.push({ text: "⚠️ " + args.map(formatArg).join(" "), isError: false });
        },
        error: (...args) => {
          rawLogs.push({ text: "❌ " + args.map(formatArg).join(" "), isError: true });
        },
      };

      try {
        // Execute inside an isolated async scope with mocked console
        const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
        const runner = new AsyncFunction("console", `"use strict";\n${code}`);
        const result = await runner(customConsole);

        if (result !== undefined) {
          rawLogs.push({ text: formatArg(result), isError: false });
        }

        if (isCancelled) return;

        // Split multi-line logs into individual line items so each line gets its own line number
        const lineList = [];
        rawLogs.forEach((item) => {
          const lines = String(item.text).split(/\r?\n/);
          lines.forEach((l) => {
            lineList.push({ text: l, isError: item.isError });
          });
        });

        setOutput(
          lineList.length > 0
            ? lineList
            : [{ text: "Program executed successfully with no output.", isError: false }]
        );
      } catch (err) {
        if (isCancelled) return;
        const errLines = String(err.message || err).split(/\r?\n/);
        setOutput(
          errLines.map((l, i) => ({
            text: i === 0 ? `❌ ${err.name || "Error"}: ${l}` : `   ${l}`,
            isError: true,
          }))
        );
      }
    };

    runJS();

    return () => {
      isCancelled = true;
    };
  }, [code]);

  // Format output with line numbers and error highlighting (identical to Python)
  const formatOutput = (outputLines) => {
    if (!outputLines || outputLines.length === 0) return null;

    return outputLines.map((item, index) => {
      const lineNumber = index + 1;
      const isErr = item.isError || item.text.startsWith("❌");
      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
            fontSize: "0.9rem",
            lineHeight: 1.6,
            mb: 0.3,
            backgroundColor: isErr
              ? isDarkMode
                ? "rgba(255, 107, 107, 0.08)"
                : "rgba(207, 34, 46, 0.06)"
              : "transparent",
            borderRadius: "4px",
            px: 0.5,
          }}
        >
          <Box
            sx={{
              color: isDarkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
              minWidth: "32px",
              textAlign: "right",
              paddingRight: 1.5,
              userSelect: "none",
              fontSize: "0.82rem",
            }}
          >
            {lineNumber}
          </Box>
          <Box
            sx={{
              flex: 1,
              color: isErr ? errorText : outputText,
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              fontWeight: isErr ? 600 : 400,
            }}
          >
            {item.text}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "8px",
        p: 2,
        background: outputBg,
        fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
        fontSize: "0.9rem",
        overflowY: "auto",
        border: `1px solid ${
          isDarkMode ? "rgba(255,255,255,0.08)" : muiTheme.palette.divider
        }`,
        transition: "background 0.3s ease, border-color 0.3s ease",
        scrollbarWidth: "thin",
        scrollbarColor: isDarkMode
          ? "rgba(255, 255, 255, 0.2) transparent"
          : "rgba(0, 0, 0, 0.2) transparent",
        "&::-webkit-scrollbar": { width: "6px", height: "6px" },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
          borderRadius: "10px",
        },
      }}
    >
      {loading ? (
        <Typography sx={{ color: isDarkMode ? "#58a6ff" : "#0969da", fontFamily: "inherit" }}>
          ⏳ Executing JavaScript...
        </Typography>
      ) : output.length > 0 ? (
        formatOutput(output)
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
            textAlign: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: "inherit" }}>
            💡 Output will appear here after execution
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: isDarkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.3)" }}
          >
            Tip: Press <strong>Ctrl + Enter</strong> to run anytime
          </Typography>
        </Box>
      )}
    </Box>
  );
}
